export const dataEngineeringPageData = {
  hero: {
    title: 'Data Engineering Solutions',
    description:
      'The foundation every dashboard stands on. We build the pipelines, models and governance that turn scattered operational data into something you can trust, query and act on.',
  },
  overview: {
    title: 'Why Data Engineering Comes First',
    paragraphs: [
      'Most analytics projects fail before a single chart is drawn. The data arrives in spreadsheets with inconsistent headers, sits in systems that were never meant to talk to each other, and changes shape without warning. No amount of visualisation fixes that.',
      'We build the layer underneath: ingestion that validates as it loads, schemas that evolve without breaking what came before, and pipelines that run on a schedule rather than on someone remembering. Every row is traceable back to the file and the person it came from.',
      'The result is a warehouse your analysts can query with confidence and your dashboards can refresh against automatically, whether the source is an Excel upload from a branch office or a nightly extract from a core system.',
    ],
    stats: [
      { number: '10MB', label: 'Per-File Ingestion Limit, Validated Row by Row' },
      { number: '100%', label: 'Uploads Checked Before Any Row Is Committed' },
      { number: '24/7', label: 'Scheduled Pipeline Monitoring' },
      { number: 'Multi', label: 'Tenant Isolation Enforced at the Data Layer' },
    ],
  },
  services: {
    title: 'Our Data Engineering Services',
    items: [
      {
        icon: '🧱',
        title: 'Schema Design & Modelling',
        description:
          'Define the tables your business actually works in, with typed fields, references between them and versioning as they evolve.',
        features: [
          'Typed columns with validation rules',
          'Referential links between master and transactional data',
          'Versioned schema history',
          'Auto-generated sequence identifiers',
          'Normalised column metadata',
        ],
        cta: 'Discuss Your Data Model',
      },
      {
        icon: '📥',
        title: 'Ingestion & Validation',
        description:
          'Load data from Excel, CSV and operational systems with every row checked before it is committed, and rejects returned for correction.',
        features: [
          'Template-driven Excel and CSV upload',
          'Row-level validation with per-row outcomes',
          'Rejected rows returned as a correction workbook',
          'Duplicate detection on fields you nominate',
          'Preview before commit on every bulk import',
        ],
        cta: 'See the Ingestion Flow',
      },
      {
        icon: '⚙️',
        title: 'Pipelines & Orchestration',
        description:
          'Queue-backed ETL that transforms and loads on a schedule, with locking so the same job never runs twice over itself.',
        features: [
          'Queue-based job orchestration',
          'Distributed locking and deduplication',
          'Automatic retry with exponential backoff',
          'Incremental and full refresh modes',
          'Job monitoring and failure alerting',
        ],
        cta: 'Talk About Your Pipelines',
      },
      {
        icon: '🔐',
        title: 'Governance & Audit',
        description:
          'Know who changed what and when, with tenant isolation enforced in the data layer rather than only in the interface.',
        features: [
          'Per-tenant data isolation',
          'Activity logging across uploads and edits',
          'Soft deletion with recovery',
          'Role-based access to administrative operations',
          'Schema change history retained by version',
        ],
        cta: 'Review Your Governance',
      },
    ],
  },
  process: {
    title: 'How We Build It',
    steps: [
      { number: 1, title: 'Source Audit', description: 'We catalogue every system, spreadsheet and manual process your data currently lives in, and establish what is authoritative where the same fact appears in several places.' },
      { number: 2, title: 'Model Design', description: 'Agree the entities, their fields and the relationships between them, so the warehouse reflects how your organisation actually operates rather than how any one system happens to store it.' },
      { number: 3, title: 'Ingestion Build', description: 'Implement loading from each source with validation rules applied at the point of entry, so bad data is caught and returned for correction rather than quietly accepted.' },
      { number: 4, title: 'Pipeline Automation', description: 'Move the transformation and load steps onto a schedule, with locking, retries and monitoring so the pipeline runs without anyone watching it.' },
      { number: 5, title: 'Validation & Reconciliation', description: 'Reconcile loaded totals against the source systems and agree the checks that will run on every subsequent refresh.' },
      { number: 6, title: 'Handover & Support', description: 'Document the model, train your team on the upload and correction workflow, and stay on for the first cycles while it beds in.' },
    ],
  },
  technologies: {
    title: 'Technologies We Use',
    items: [
      { icon: '🐘', name: 'PostgreSQL' },
      { icon: '☁️', name: 'AWS S3' },
      { icon: '🔄', name: 'Queue-Based ETL' },
      { icon: '🐍', name: 'Python' },
      { icon: '📋', name: 'SQL' },
      { icon: '📊', name: 'Power BI' },
      { icon: '📗', name: 'Excel & CSV' },
      { icon: '🔁', name: 'Scheduled Jobs' },
    ],
  },
  cta: {
    title: 'Ready to Build a Foundation You Can Trust?',
    description:
      "Tell us where your data lives today and we will show you what it would take to make it reliable, current and queryable.",
    buttonText: 'Start the Conversation',
  },
};
